import { Link } from 'react-router-dom';
import { Page } from '../components/Layout.jsx';
import { AlertTriangle, WifiArcs } from '../components/icons.jsx';

/**
 * 404 / unknown-or-inactive journey — built to the designer's frame, Figma
 * node `4100:462`.
 *
 * The frame sits under the short 129px journey header, not the taller
 * marketing hero the contact and legal pages use: the mark, the two-part
 * heading, the ghost numeral and one orange button.
 *
 * One deliberate addition to the frame — a single line of copy above the
 * button. The design carries no supporting text at all, which reads well but
 * leaves a visitor from a switched-off ad campaign with nowhere to go.
 *
 * `reason` distinguishes a genuinely unknown URL from a campaign path that
 * exists but has been switched off, because the second is far more likely to
 * be someone we paid for and the copy should not tell them they mistyped. The
 * design has one frame, so that distinction now rides on the heading alone.
 */
export default function NotFoundPage({ reason = 'unknown' }) {
  const inactive = reason === 'inactive';
  const label = inactive ? 'Offer Not Available' : 'Page Not Found';

  return (
    <Page brand="home" callbackLabel="Call us now">
      <section className="notfound">
        <WifiArcs className="notfound__arcs" />

        <div className="notfound__inner">
          <h1 className="notfound__heading">
            <AlertTriangle size={54} />
            <span>Error</span>
            <span className="notfound__label">{label}</span>
          </h1>

          <p className="notfound__code">404</p>

          {/* The one addition to the designer's frame. It sits directly above
              the button because it is the reason to press it: most people here
              followed a paid link to a campaign that has since been switched
              off, and a dead end with a single generic button wastes the
              click we paid for. */}
          <p className="notfound__lede">
            {inactive
              ? 'That offer has ended, but you can still compare what’s available at your address.'
              : 'The link may be out of date, but you can still compare what’s available at your address.'}
          </p>

          <Link className="button notfound__button" to="/">
            Return to Home
          </Link>
        </div>
      </section>
    </Page>
  );
}
